import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Container } from '@material-ui/core';
import VectorSearch from './VectorSearch';
import { GetActiveFilters, FilterTags } from '../../signals/filterService';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  examplesContainer: {
    marginTop: theme.spacing(6),
    maxWidth: '900px',
    margin: '0 auto',
  },
  examplesTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  exampleItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#fff',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
    },
  },
}));

const ConversationalUI: React.FC = () => {
  const classes = useStyles();
  const [selectedQuestion] = useState('');

  // State for filters extracted from the application's filter service
  const [facilityNames, setFacilityNames] = useState<string[]>([]);
  const [classificationNames, setClassificationNames] = useState<string[]>([]);
  const [classificationTypes, setClassificationTypes] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Effect to extract filter values from the application's filter service
  useEffect(() => {
    // Get all active filters from the filter service
    const activeFilters = GetActiveFilters();

    if (activeFilters && activeFilters.length > 0) {
      // Extract facility names
      const facilities = activeFilters
        .filter(filter => filter.property === FilterTags.report.facility)
        .map(filter => filter.value);
      setFacilityNames(facilities);

      // Extract classification types (Topic, Hazard, Root Cause)
      const types: string[] = [];

      // Check if Topic filter is active
      if (activeFilters.some(filter => filter.property === FilterTags.report.topic)) {
        types.push('Topic');
      }

      // Check if Hazard filter is active
      if (activeFilters.some(filter => filter.property === FilterTags.report.hazard)) {
        types.push('Hazard');
      }

      // Check if Root Cause filter is active
      if (activeFilters.some(filter => filter.property === FilterTags.report.rootCause)) {
        types.push('RootCause');
      }

      setClassificationTypes(types);

      // Extract classification names from Topic, Hazard, and Root Cause filters
      const names = activeFilters
        .filter(filter => (
          filter.property === FilterTags.report.topic ||
          filter.property === FilterTags.report.hazard ||
          filter.property === FilterTags.report.rootCause
        ))
        .map(filter => filter.value);
      setClassificationNames(names);

      // Extract date range filters if they exist
      // Note: This assumes the application has date filters in a specific format
      // You may need to adjust this based on how dates are stored in your application
      const fromDateFilter = activeFilters.find(filter => filter.name === 'From');
      if (fromDateFilter) {
        setStartDate(fromDateFilter.value);
      }

      const toDateFilter = activeFilters.find(filter => filter.name === 'To');
      if (toDateFilter) {
        setEndDate(toDateFilter.value);
      }
    }
  }, []);
  
  return (
    <div className={classes.root}>
      <Container maxWidth="lg">
        <VectorSearch
          selectedQuestion={selectedQuestion}
          facilityNames={facilityNames}
          classificationNames={classificationNames}
          classificationTypes={classificationTypes}
          startDate={startDate}
          endDate={endDate}
        />
      </Container>
    </div>
  );
};

export default ConversationalUI;
